/**
 * API client for the Sales Comps module.
 */

import api from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CompSale {
  parcel_id: string;
  address: string;
  city: string;
  sale_date: string | null;
  sale_price: number | null;
  price_psf: number | null;
  building_sf: number | null;
  land_sf: number | null;
  year_built: number | null;
  floor_count: number | null;
  unit_count: number | null;
  is_condo: boolean;
  dor_code: string;
  dor_description: string;
  property_type: string;
  latitude: number;
  longitude: number;
  county: string;
  qualified_sale: boolean | null;
}

export interface CompSearchRequest {
  geometry: { rings: number[][][] };
  county: string;
  property_types: string[];
  date_start?: string | null;
  date_end?: string | null;
  min_price?: number | null;
  max_price?: number | null;
  qualified_only?: boolean;
  exclude_condos?: boolean;
  min_building_sf?: number | null;
  max_building_sf?: number | null;
  min_year_built?: number | null;
  max_year_built?: number | null;
  min_price_psf?: number | null;
  max_price_psf?: number | null;
}

export interface CompSearchResponse {
  results: CompSale[];
  total_count: number;
  county: string;
  truncated: boolean;
  avg_price_psf: number | null;
  median_sale_price: number | null;
}

export interface CountyInfo {
  name: string;
  state: string;
  active: boolean;
  provider_type?: string;
}

export interface PropertyTypeInfo {
  name: string;
  dor_codes: string[];
  color: string;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function fetchCounties(): Promise<CountyInfo[]> {
  const { data } = await api.get("/api/comps/counties");
  return data;
}

export async function fetchPropertyTypes(): Promise<PropertyTypeInfo[]> {
  const { data } = await api.get("/api/comps/property-types");
  return data;
}

export async function searchComps(
  request: CompSearchRequest
): Promise<CompSearchResponse> {
  const { data } = await api.post("/api/comps/search", request);
  return data;
}

export async function fetchPropertyDetail(
  county: string,
  parcelId: string
): Promise<CompSale> {
  const { data } = await api.get(
    `/api/comps/property/${encodeURIComponent(county)}/${encodeURIComponent(parcelId)}`
  );
  return data;
}
